/**
 * Ethos Virtual — Plan Definitions
 *
 * Two product lines share one entitlement model:
 *
 *  1. Main plans (business / enterprise / corporate) — higher-tier, $999+,
 *     sold via the standard checkout (/api/webhooks/stripe → /onboarding).
 *  2. SMB self-serve tiers (starter / standard / high-volume) — $69 / $139 /
 *     $199 USD (pricing v5, 2026-07-07), sold via Stripe Payment Links (/api/webhooks/stripe-ethos).
 *
 * OVERAGE V6 (decision made by Kurt 2026-07-08): SMB overage billing is
 * TIERED, matching the marketing display rates: 0.25 (Starter) / 0.24
 * (Standard) / 0.23 (High-Volume) per additional minute, same numerals in
 * USD and CAD. This resolves the old open question of flat 0.45 billing vs
 * tiered display; the flat 0.45 rate is retired. Business (0.44) and
 * Enterprise (0.35) overage rates are unchanged.
 *
 * Plan entitlements live at the organization level. Plan gating reads these
 * definitions as the single source of truth — never hardcode limits elsewhere.
 *
 * WHITE-LABEL RULE: Plan names are first-party branded.
 * Never reference provider plan tiers or pricing structures.
 */

export const PLAN_SLUGS = {
  // Main plans
  BUSINESS: "business",
  ENTERPRISE: "enterprise",
  CORPORATE: "corporate",
  // SMB self-serve tiers
  STARTER: "starter",
  STANDARD: "standard",
  HIGH_VOLUME: "high-volume",
} as const;

export type PlanSlug = (typeof PLAN_SLUGS)[keyof typeof PLAN_SLUGS];

export interface PlanDefinition {
  slug: PlanSlug;
  name: string;
  tagline: string;
  priceUSD: number;
  /** If true, price is not published on the website (contact sales) */
  priceHidden?: boolean;
  includedMinutes: number;
  overageRateUSD: number;
  /** Max AI receptionist agents this plan may provision. */
  maxAgents: number;
  /**
   * Max distinct languages an agent may be configured for. SMB tiers are
   * capped at 2 (Starter: 1); main plans scale up. Availability is still
   * subject to Ethos Virtual approval — not all languages are guaranteed.
   */
  maxLanguages: number;
  maxTeamMembers: number;
  /** true for the SMB self-serve product line. */
  isSmb?: boolean;
  /** True when the plan handles incoming calls only (Starter). */
  inboundOnly?: boolean;
  /**
   * Optional two-way (inbound + outbound dialing) add-on. Buying it adds
   * `priceUSD` to the monthly fee and raises the agent cap to `maxAgents`
   * (one inbound + one outbound). Standard only.
   */
  twoWayAddOn?: { priceUSD: number; maxAgents: number };
  tier: 1 | 2 | 3 | 4 | 5 | 6;
}

export const PLANS: Record<PlanSlug, PlanDefinition> = {
  // ─── SMB self-serve tiers ────────────────────────────────────────────────
  [PLAN_SLUGS.STARTER]: {
    slug: PLAN_SLUGS.STARTER,
    name: "Starter",
    tagline: "One receptionist, always on",
    priceUSD: 69,
    includedMinutes: 50,
    overageRateUSD: 0.25,
    maxAgents: 1,
    maxLanguages: 1,
    maxTeamMembers: 2,
    isSmb: true,
    inboundOnly: true,
    tier: 1,
  },
  [PLAN_SLUGS.STANDARD]: {
    slug: PLAN_SLUGS.STANDARD,
    name: "Standard",
    tagline: "More minutes, more reach",
    priceUSD: 139,
    includedMinutes: 150,
    overageRateUSD: 0.24,
    maxAgents: 1,
    maxLanguages: 2,
    maxTeamMembers: 2,
    isSmb: true,
    twoWayAddOn: { priceUSD: 60, maxAgents: 2 },
    tier: 2,
  },
  [PLAN_SLUGS.HIGH_VOLUME]: {
    slug: PLAN_SLUGS.HIGH_VOLUME,
    name: "High-Volume",
    tagline: "Built for busy phones",
    priceUSD: 199,
    includedMinutes: 300,
    overageRateUSD: 0.23,
    maxAgents: 1,
    maxLanguages: 2,
    maxTeamMembers: 2,
    isSmb: true,
    tier: 3,
  },
  // ─── Main plans ──────────────────────────────────────────────────────────
  [PLAN_SLUGS.BUSINESS]: {
    slug: PLAN_SLUGS.BUSINESS,
    name: "Business",
    tagline: "Scale your capabilities",
    priceUSD: 699,
    includedMinutes: 100,
    overageRateUSD: 0.44,
    maxAgents: 5,
    maxLanguages: 3,
    maxTeamMembers: 2,
    tier: 4,
  },
  [PLAN_SLUGS.ENTERPRISE]: {
    slug: PLAN_SLUGS.ENTERPRISE,
    name: "Enterprise",
    tagline: "Do more with enterprise",
    priceUSD: 999,
    includedMinutes: 250,
    overageRateUSD: 0.35,
    maxAgents: 10,
    maxLanguages: 5,
    maxTeamMembers: 25,
    tier: 5,
  },
  [PLAN_SLUGS.CORPORATE]: {
    slug: PLAN_SLUGS.CORPORATE,
    name: "Corporate",
    tagline: "Fully customizable for your organization",
    priceUSD: 0, // Custom pricing — determined per engagement
    priceHidden: true,
    includedMinutes: 500,
    overageRateUSD: 0.28,
    maxAgents: 20,
    maxLanguages: 10,
    maxTeamMembers: 100,
    tier: 6,
  },
};

/** The SMB self-serve tier slugs, in display order. */
export const SMB_PLAN_SLUGS: PlanSlug[] = [
  PLAN_SLUGS.STARTER,
  PLAN_SLUGS.STANDARD,
  PLAN_SLUGS.HIGH_VOLUME,
];

/**
 * Map an SMB tier key (as used by SmbPlansDropdown / the stripe-ethos webhook,
 * where "high-volume" is the key) to its PlanSlug. They are identical strings
 * today, but this indirection keeps the webhook contract and the plan model
 * decoupled.
 */
export const SMB_TIER_KEY_TO_PLAN_SLUG: Record<string, PlanSlug> = {
  starter: PLAN_SLUGS.STARTER,
  standard: PLAN_SLUGS.STANDARD,
  "high-volume": PLAN_SLUGS.HIGH_VOLUME,
};

/**
 * Effective agent cap for an org: the plan's cap, raised to the add-on cap
 * when the org purchased the plan's optional two-way add-on (Standard only;
 * `hasTwoWayAddOn` comes from organizations.two_way_addon).
 */
export function effectiveMaxAgents(
  plan: PlanDefinition,
  hasTwoWayAddOn: boolean
): number {
  return hasTwoWayAddOn && plan.twoWayAddOn
    ? plan.twoWayAddOn.maxAgents
    : plan.maxAgents;
}

/** Type guard for a valid plan slug. */
export function isPlanSlug(value: unknown): value is PlanSlug {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(PLANS, value)
  );
}
